import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { AppConfigurationService } from '../api/services/AppConfigurationService';
import { useAuth } from './AuthContext';
import type { Routemize_DTOs_AppConfiguration_AppConfigurationDto } from '../api/models/Routemize_DTOs_AppConfiguration_AppConfigurationDto';

const APP_CONFIG_STORAGE_KEY = 'app_config';
const APP_CONFIG_VERSION_KEY = 'app_config_version';
const CURRENT_VERSION = '2.0'; // Increment this when the config structure changes (2.0: /api/app/appConfiguration, six sections)

interface AppConfigContextType {
  config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null;
  isLoading: boolean;
  error: string | null;
  refreshConfig: (forceLoad?: boolean) => Promise<Routemize_DTOs_AppConfiguration_AppConfigurationDto | null>;
  updateCurrentUser: (updates: Record<string, unknown>) => void;
}

export const AppConfigContext = createContext<AppConfigContextType | undefined>(undefined);

export const useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (context === undefined) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
};

interface AppConfigProviderProps {
  children: ReactNode;
}

export const AppConfigProvider: React.FC<AppConfigProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const previousAuthStateRef = useRef<boolean | null>(null);
  const [config, setConfig] = useState<Routemize_DTOs_AppConfiguration_AppConfigurationDto | null>(() => {
    try {
      const storedVersion = localStorage.getItem(APP_CONFIG_VERSION_KEY);
      const storedConfig = localStorage.getItem(APP_CONFIG_STORAGE_KEY);
      
      if (storedVersion === CURRENT_VERSION && storedConfig) {
        return JSON.parse(storedConfig);
      }
    } catch (error) {
      // Error loading stored config
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(!config);
  const [error, setError] = useState<string | null>(null);
  const [hasLoadedInitialConfig, setHasLoadedInitialConfig] = useState(() => {
    try {
      const storedVersion = localStorage.getItem(APP_CONFIG_VERSION_KEY);
      const storedConfig = localStorage.getItem(APP_CONFIG_STORAGE_KEY);
      return !!(storedVersion === CURRENT_VERSION && storedConfig);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const storedVersion = localStorage.getItem(APP_CONFIG_VERSION_KEY);
    const storedConfig = localStorage.getItem(APP_CONFIG_STORAGE_KEY);
    if (storedVersion === CURRENT_VERSION && storedConfig) {
      setHasLoadedInitialConfig(true);
    }
  }, []);

  const saveConfig = (newConfig: Routemize_DTOs_AppConfiguration_AppConfigurationDto) => {
    try {
      localStorage.setItem(APP_CONFIG_STORAGE_KEY, JSON.stringify(newConfig));
      localStorage.setItem(APP_CONFIG_VERSION_KEY, CURRENT_VERSION);
    } catch (error) {
      // Error saving config
    }
  };

  const clearStoredConfig = () => {
    try {
      localStorage.removeItem(APP_CONFIG_STORAGE_KEY);
      localStorage.removeItem(APP_CONFIG_VERSION_KEY);
    } catch (error) {
      // Error clearing config
    }
  };

  const fetchConfig = async (
    forceLoad: boolean = false,
  ): Promise<Routemize_DTOs_AppConfiguration_AppConfigurationDto | null> => {
    // /api/app/appConfiguration doesn't require authentication. It provides the
    // granted policies, current user/tenant, client settings and extra properties.
    if(!hasLoadedInitialConfig || forceLoad)
    {
      setIsLoading(true);
      setError(null);

      try {
        const configData = await AppConfigurationService.getApiAppAppConfiguration();
        setConfig(configData);
        saveConfig(configData);
        setHasLoadedInitialConfig(true);
        // Returned directly (not just set into state) so a caller that needs the
        // freshly authenticated result right away — e.g. EmbedTokenRoute deciding
        // whether to render — doesn't have to wait for a re-render to read it back
        // out of this context.
        return configData;
      } catch (err) {
        // Error fetching application configuration
        setError('Failed to load application configuration');
        clearStoredConfig();
        return null;
      } finally {
        setIsLoading(false);
      }
    }
    return config;
  };

  const updateCurrentUser = (updates: Record<string, unknown>) => {
    setConfig(prev => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        currentUser: {
          ...prev.currentUser,
          ...updates
        }
      };
      saveConfig(updated);
      return updated;
    });
  };

  // Load config on app startup if no stored config exists
  useEffect(() => {
    if (!hasLoadedInitialConfig) {
      fetchConfig();
    }
  }, [hasLoadedInitialConfig]);

  // Refresh config only when transitioning from unauthenticated to authenticated
  useEffect(() => {
    const previousAuthState = previousAuthStateRef.current;
    
    // Track authentication state changes
    if (previousAuthState !== null) {
      // Only refresh config when transitioning from false -> true (login)
      // This gets updated currentUser info and avoids redundant calls on page loads
      if (!previousAuthState && isAuthenticated && hasLoadedInitialConfig && config) {
        fetchConfig(true);
      }
    }
    
    // Update the previous auth state for next comparison
    previousAuthStateRef.current = isAuthenticated;
  }, [isAuthenticated, hasLoadedInitialConfig, config]);

  return (
    <AppConfigContext.Provider value={{ config, isLoading, error, refreshConfig: fetchConfig, updateCurrentUser }}>
      {children}
    </AppConfigContext.Provider>
  );
};